import { FiChevronRight } from "react-icons/fi";
import { Link } from "react-router";
import { Avatar } from "~/components/Avatar";
import { SectionHeading } from "~/components/SectionHeading";
import { SkeletonImage } from "~/components/SkeletonImage";
import { Text } from "~/components/Text";
import GoldenButterImage from "~/images/golden-butter.png";
import { SetAction } from "~/hooks/use-action";
import { PlanActions } from "./route";

type Club = {
  id: string;
  name: string;
  imageUrl: string;
  headline: string;
  premium: boolean;
  foundingMember: boolean;
};

type Host = {
  primary: boolean;
  user?: {
    id: string;
    name: string;
    firstName: string;
    bio?: string | null;
    headline?: string | null;
    suburb?: string | null;
    profileImageUrl?: string | null;
    foundingMember: boolean;
  } | null;
};

export function ClubCard({ club }: { club: Club }) {
  return (
    <div>
      <Text variant="primary" size="xs" as="h3" className="mb-2">
        Meet the host
      </Text>
      <Link
        to={`/clubs/${club.id}`}
        className="group flex items-center gap-3 text-left"
      >
        <SkeletonImage
          src={club.imageUrl}
          alt={club.name}
          className="h-12 w-12 flex-shrink-0 rounded-md"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <Text variant="secondary" size="sm" weight="bold" className="truncate">
              {club.name}
            </Text>
            {(club.premium || club.foundingMember) && (
              <img
                src={GoldenButterImage}
                alt={club.premium ? "Gold host" : "Founding member"}
                className="h-3.5 w-3.5 flex-shrink-0"
              />
            )}
          </div>
          {club.headline && (
            <Text variant="secondary" size="xs" className="line-clamp-2">
              {club.headline}
            </Text>
          )}
        </div>
        <FiChevronRight
          size={16}
          className="flex-shrink-0 text-butter-green transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </Link>
    </div>
  );
}

type HostCardsProps = {
  hosts: Host[];
  clubId?: string;
  onSetAction: SetAction<PlanActions>;
  className?: string;
};

export function HostCards({
  hosts,
  clubId,
  onSetAction,
  className,
}: HostCardsProps) {
  const validHosts = hosts.filter((h) => h.user);
  if (validHosts.length === 0) return null;

  const heading =
    validHosts.length === 1 ? "Meet the planner" : "Meet the planners";

  return (
    <section className={`flex flex-col gap-4 ${className ?? ""}`}>
      <SectionHeading>{heading}</SectionHeading>
      <div className="flex flex-col gap-3">
        {validHosts.map((host) => {
          const content = (
            <>
              <Avatar
                user={{
                  firstName: host.user!.firstName,
                  name: host.user!.name,
                  profileImageUrl: host.user!.profileImageUrl ?? undefined,
                }}
                size={56}
                className="flex-shrink-0 rounded-xl"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <Text
                    variant="secondary"
                    size="md"
                    weight="bold"
                    className="truncate"
                  >
                    {host.user!.name}
                  </Text>
                  {host.user!.foundingMember && (
                    <img
                      src={GoldenButterImage}
                      alt="Founding member"
                      className="h-4 w-4 flex-shrink-0"
                    />
                  )}
                </div>
                {(() => {
                  const parts = [host.user!.headline, host.user!.suburb].filter(
                    (p): p is string => Boolean(p && p.trim().length > 0),
                  );
                  if (parts.length === 0) return null;
                  return (
                    <Text variant="secondary" size="sm" className="truncate">
                      {parts.join(" · ")}
                    </Text>
                  );
                })()}
              </div>
              <FiChevronRight
                size={16}
                className="flex-shrink-0 text-butter-green transition-transform group-hover:translate-x-0.5"
                aria-hidden
              />
            </>
          );
          return (
            <div key={host.user!.id} className="flex flex-col gap-2">
              {clubId ? (
                <Link
                  to={`/clubs/${clubId}`}
                  className="group flex w-full items-center gap-3 text-left"
                >
                  {content}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    onSetAction(undefined, {
                      type: "view-event-host",
                      data: undefined,
                    })
                  }
                  className="group flex w-full items-center gap-3 text-left"
                >
                  {content}
                </button>
              )}
              {host.user!.bio && (
                <Text
                  variant="secondary"
                  size="md"
                  className="whitespace-pre-line leading-relaxed"
                >
                  {host.user!.bio}
                </Text>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
